import { inject, injectable } from "inversify";
import { VideoLayer } from "@manager/layer/VideoLayer";
import { MusicPlayer } from "@manager/scenarioRunner/musicManager/MusicPlayer";
import { VolumeTransitionManager } from "@manager/scenarioRunner/musicManager/VolumeTransitions";
import { Soundtrack } from "../../../model/playerConfiguration/Soundtrack";
import { TYPES } from "../../di/types";

export interface PlayerMusicManager {
  readonly currentTime: number;
  playbackRate: number;
  src: string;
  waitOnBuffering: boolean;
  syncOnPlay: boolean;
  play();
  pause();
  setDelay(delay: number);
  loadSoundtrackTransitions(soundtrack: Soundtrack);
  resetTransitions();
  addVolumeTransition(videoTime: number, targetVolume: number, duration?: number);
}

@injectable()
export class PlayerMusicManagerImpl implements PlayerMusicManager {
  private static INTERVAL: number = 30;

  // Volume transitions can be applied even after the video has ended,
  // so we keep track of how much time has passed after the video has ended
  private afterEndTime: number = 0;

  private lastUpdateTime: number = 0;

  // We keep track of if the player should be playing to ensure that music starts / pauses
  // ( Included because of some bugs on Safari )
  private shouldBePlaying: boolean = false;

  private startDelay: number = 0;
  private initialized: boolean = false;

  syncOnPlay: boolean = true;
  waitOnBuffering: boolean = true;

  constructor(
    // TODO Replace injection
    @inject(TYPES.music.VolumeTransitionManager) protected volumeTransitionManager: VolumeTransitionManager,
    @inject(TYPES.music.MusicPlayer) protected musicPlayer: MusicPlayer,
    @inject(TYPES.layer.VideoLayer) protected videoLayer: VideoLayer,
  ) {
    this.volumeTransitionManager.updateTransitions(0);
  }

  get src(): string {
    return this.musicPlayer.src;
  }

  set src(src: string) {
    this.musicPlayer.src = src;
  }

  set playbackRate(playbackRate: number) {
    this.musicPlayer.playbackRate = playbackRate;
  }
  get playbackRate(): number {
    return this.musicPlayer.playbackRate;
  }

  private get player() {
    return this.videoLayer.getPlayer();
  }

  get currentTime() {
    return this.videoLayer.getCurrentSeekTime() + this.afterEndTime;
  }

  setDelay(delay: number) {
    this.startDelay = delay;
  }

  pause(): void {
    this.shouldBePlaying = false;
    this.update();
  }

  play(): void {
    this.shouldBePlaying = true;

    if (!this.initialized) {
      // We want to install it on the first play, because media can be in uninitialized state
      // and if we try to get currentTime or volume it will throw an error
      this.installLoop();

      this.initialized = true;
    }

    this.update();
  }

  loadSoundtrackTransitions(soundtrack: Soundtrack) {
    this.resetTransitions();

    const { fadeInDuration, startOffset, endOffset, fadeOutDuration } = soundtrack;

    if (startOffset) {
      this.setDelay(startOffset);
    }

    if (fadeInDuration) {
      this.addVolumeTransition(-1, 0, 0);
      this.addVolumeTransition(this.startDelay, 1, fadeInDuration);
    }

    if (fadeOutDuration) {
      this.player.one("loadedmetadata", () => {
        this.addVolumeTransition(this.player.duration() - fadeOutDuration - endOffset, 0, fadeOutDuration);
      });
    }
  }

  resetTransitions() {
    this.startDelay = 0;
    this.volumeTransitionManager.resetTransitions();
  }

  addVolumeTransition(videoTime: number, targetVolume: number, duration?: number) {
    this.volumeTransitionManager.addTransition(videoTime, targetVolume, duration);
  }

  private sync(maxAcceptableTimeDifference: number = 0): void {
    const desiredAudioTime = this.player.currentTime() - this.startDelay;

    if (Math.abs(this.musicPlayer.currentTime - desiredAudioTime) > maxAcceptableTimeDifference) {
      this.musicPlayer.currentTime = desiredAudioTime;
    }
  }

  private update(): void {
    const waitForPlayer = this.waitOnBuffering ? this.videoLayer.getIsWaiting() && this.player.readyState() <= 2 : false;

    this.updateTimes();

    if (this.shouldBePlaying && this.currentTime >= this.startDelay && !waitForPlayer) {
      if (!this.musicPlayer.isPlaying && this.syncOnPlay) {
        this.sync(0.1);
      }

      // We don't want to start playing if the video is paused (e.g. pause happens during end, and we don't want
      // it to play until video has resumed, which means we wait for video.paused to be false ).
      // At the same time we don't want to pause if the music is already playing ( happens e.g. during scenario change )
      if (!this.player.paused()) {
        this.musicPlayer.play();
      }
    } else {
      this.musicPlayer.pause();
    }

    this.volumeTransitionManager.baseVolume = this.videoLayer.getVolume();
    this.volumeTransitionManager.updateTransitions(this.currentTime);
  }

  private updateTimes() {
    if (this.player.ended()) {
      const deltaTime = performance.now() - this.lastUpdateTime;

      this.afterEndTime += deltaTime / 1000;

      this.lastUpdateTime = performance.now();
    } else {
      this.afterEndTime = 0;
    }
  }

  private installLoop() {
    this.lastUpdateTime = performance.now();

    setInterval(this.update.bind(this), PlayerMusicManagerImpl.INTERVAL);
  }
}
